// Layer + view state shared across the panels and the 3D viewport.
//
// One zustand store keeps the panels (which write) and the viewer
// (which reads) decoupled. Everything is local — no backend, no
// persistence (localStorage comes later for client annotations).

import { create } from "zustand";

export interface ToleranceSummary {
  validCount: number;
  withinCount: number;
  /** within / valid in [0, 1]; NaN when validCount = 0. */
  fraction: number;
  thresholdC: number;
}

export interface SelectedFace {
  /** 0-based face index into the per-pass face_temp.f32 array. */
  faceId: number;
  /** Pass label this face was sampled from (e.g. "W1"). */
  passLabel: string;
  /** Apparent temperature in °C. null = NaN (face had no thermal coverage). */
  apparentTempC: number | null;
  /** UTM-frame coordinates (x = easting, y = northing, z = elevation).
   *  null when mesh_meta.json hasn't loaded yet. Internal — not
   *  surfaced in the panel; the viewer projects this to lat/lon. */
  utm: [number, number, number] | null;
  /** Decimal-degree [lat, lon] derived from UTM via proj4. null if
   *  no UTM available, or projection failed. */
  latLon: [number, number] | null;
  /** Elevation in metres (= UTM Z). null when not available. */
  elevationM: number | null;
  /** Mesh-local world point — anchors the visual marker in the viewport. */
  worldPoint: [number, number, number];
  /** True if this face is in the operator-painted low-emissivity set. */
  isLowEmissivity: boolean;
}

export interface ThermalSummary {
  /** Slider min = palette anchor min. Set to the typical-data low
   *  (p01) so the slider's full track maps to the range users care
   *  about. Outliers below this fall through to RGB by default. */
  rangeMin: number;
  /** Slider max = palette anchor max. Typical-data high (p99). */
  rangeMax: number;
  /** Active mode driving the legend ("°C" vs "ΔT °C"). */
  mode: "absolute" | "relative";
  /** Radius in meters when mode === "relative". */
  radiusM?: number;
  /** Total face count from the bundle. */
  faceCount: number;
  /** Faces with finite values for the active mode. */
  validCount: number;
}

export type ViewMode = "absolute" | "relative";

export const SCALE_RADII = [0.3, 0.7, 1.5, 3.0, 5.0] as const;
export type ScaleRadius = (typeof SCALE_RADII)[number];

export const TOLERANCE_THRESHOLDS = [0.5, 1.0, 2.0] as const;
export type ToleranceThreshold = (typeof TOLERANCE_THRESHOLDS)[number];

interface ViewerState {
  thermalOn: boolean;
  thermalOpacity: number; // 0..1

  viewMode: ViewMode;
  scaleRadius: ScaleRadius;

  toleranceOn: boolean;
  toleranceOpacity: number;
  toleranceThreshold: ToleranceThreshold;

  pinsOn: boolean;

  /** Active filter band — only faces whose displayed value falls in
   *  [bandLo, bandHi] receive the Lava color overlay; out-of-band
   *  faces fall through to RGB. Same control used in both modes;
   *  resets to the full data range when mode or scale changes. */
  bandLo: number;
  bandHi: number;

  thermalSummary: ThermalSummary | null;
  /** Whether the active bundle ships face_truth_residual.f32. When
   *  false, the Tolerance layer toggle is hidden (no data to compare
   *  against). */
  toleranceAvailable: boolean;
  toleranceSummary: ToleranceSummary | null;
  selectedFace: SelectedFace | null;

  setThermalOn: (on: boolean) => void;
  setThermalOpacity: (op: number) => void;
  setViewMode: (mode: ViewMode) => void;
  setScaleRadius: (r: ScaleRadius) => void;
  setToleranceOn: (on: boolean) => void;
  setToleranceOpacity: (op: number) => void;
  setToleranceThreshold: (t: ToleranceThreshold) => void;
  setPinsOn: (on: boolean) => void;
  setBand: (lo: number, hi: number) => void;
  setThermalSummary: (s: ThermalSummary | null) => void;
  setToleranceAvailable: (avail: boolean) => void;
  setToleranceSummary: (s: ToleranceSummary | null) => void;
  setSelectedFace: (f: SelectedFace | null) => void;
}

export const useViewerStore = create<ViewerState>((set) => ({
  thermalOn: false,
  thermalOpacity: 0.85,

  viewMode: "absolute",
  scaleRadius: 1.5,

  toleranceOn: false,
  toleranceOpacity: 0.85,
  toleranceThreshold: 1.0,

  pinsOn: true,

  bandLo: -Infinity,
  bandHi: Infinity,

  thermalSummary: null,
  toleranceAvailable: false,
  toleranceSummary: null,
  selectedFace: null,

  setThermalOn: (on) => set({ thermalOn: on }),
  setThermalOpacity: (op) => set({ thermalOpacity: op }),
  setViewMode: (viewMode) => set({ viewMode }),
  setScaleRadius: (scaleRadius) => set({ scaleRadius }),
  setToleranceOn: (on) => set({ toleranceOn: on }),
  setToleranceOpacity: (op) => set({ toleranceOpacity: op }),
  setToleranceThreshold: (toleranceThreshold) => set({ toleranceThreshold }),
  setPinsOn: (on) => set({ pinsOn: on }),
  setBand: (bandLo, bandHi) => set({ bandLo, bandHi }),
  setThermalSummary: (thermalSummary) => set({ thermalSummary }),
  setToleranceAvailable: (toleranceAvailable) => set({ toleranceAvailable }),
  setToleranceSummary: (toleranceSummary) => set({ toleranceSummary }),
  setSelectedFace: (selectedFace) => set({ selectedFace }),
}));
